<!-- Task 3 static structure. Acceptance reference: PlateX — Names · Prototype 9.2.
     Nine content sections in DOM order. No statistics, no totals (Task 4).
     No seller comparison. No watchlist. No evidence chart. -->
<div class="names-root">

  <!-- ============ 1 · HERO ============ -->
  <section class="hero">
    <div class="wrap">
      <span class="eyebrow">The Collection · Names</span>
      <h1>Your name may already be a registration.</h1>
      <p class="lead">We process national name records from around the world and check every one against
        every British registration format ever issued. This is the register of the names that can be
        written exactly, on a plate that is real.</p>

      <div class="stage">
        <span class="plate xl" [attr.aria-label]="'A registration that spells ' + hero[heroI].name"><span class="ch"><ng-container
          *ngFor="let c of hero[heroI].chars"><span [class.sub]="c.sub">{{ c.ch }}</span></ng-container></span></span>
        <div class="resolve">
          <span class="arrow"></span>
          <div class="nm" [class.on]="heroNameOn">{{ hero[heroI].name }}</div>
          <div class="note" [class.on]="heroNameOn">{{ hero[heroI].note }}</div>
        </div>
        <div class="stage-cy">
          <button *ngFor="let h of hero; let i = index" [class.active]="i === heroI"
                  (click)="showHero(i, true)">{{ h.name }}</button>
        </div>
      </div>
      <!-- hero statistics intentionally absent: every statistic is live or absent (Task 4) -->
    </div>
  </section>

  <!-- ============ 2 · HOW A NAME CROSSES ============ -->
  <section class="section section--tight rule-band" id="alphabet">
    <div class="wrap">
      <div class="intro">
        <span class="eyebrow intro-eyebrow">How a name crosses</span>
        <p class="stmt intro-stmt">A name reaches the Register only if it fits a plate that was actually issued.</p>
        <p class="intro-copy">Britain has issued four families of registration and each puts its numbers in a
          different place. A name survives the crossing when the letters landing on those positions can be
          imitated by a numeral, and when the result is a series that was really printed. That is the whole
          of the rule, and it is the reason the Register is the size it is.</p>
      </div>
      <div class="fmt-grid">
        <div class="fmt">
          <div class="era">Dateless</div><div class="yrs">1903 – 1963</div>
          <div class="mask">
            <div class="sl dig">5</div><div class="sl dig">1</div><div class="gap"></div>
            <div class="sl">M</div><div class="sl">O</div><div class="sl">N</div>
          </div>
          <p class="mask-cap">Numbers first, letters after, and no date locked into either. A 5 and a 1 open the name. <b>Simon</b>.</p>
        </div>
        <div class="fmt">
          <div class="era">Suffix</div><div class="yrs">1963 – 1983</div>
          <div class="mask">
            <div class="sl">D</div><div class="sl">E</div><div class="sl">B</div><div class="gap"></div>
            <div class="sl dig">8</div><div class="sl dig">1</div><div class="sl">E</div>
          </div>
          <p class="mask-cap">An 8 for the second <b>B</b>, a 1 for the <b>I</b>; the final letter is the year. <b>Debbie</b>.</p>
        </div>
        <div class="fmt">
          <div class="era">Prefix</div><div class="yrs">1983 – 2001</div>
          <div class="mask">
            <div class="sl">P</div><div class="sl dig">3</div><div class="gap"></div>
            <div class="sl">T</div><div class="sl">E</div><div class="sl">R</div>
          </div>
          <p class="mask-cap">The leading letter dates the plate, so it has to be a letter your name already owns. <b>Peter</b>.</p>
        </div>
        <div class="fmt">
          <div class="era">Current</div><div class="yrs">2001 – today</div>
          <div class="mask">
            <div class="sl">M</div><div class="sl">A</div><div class="sl dig">7</div><div class="sl dig">1</div><div class="gap"></div>
            <div class="sl">L</div><div class="sl">D</div><div class="sl">A</div>
          </div>
          <p class="mask-cap">The numbers are an age identifier, so they must be a real date, and the letters never include I or Q. <b>Matilda</b>, issued 2021.</p>
        </div>
      </div>
      <div class="alpha-strip">
        <div class="alpha-strip-head">
          <span class="eyebrow">The plate alphabet</span>
          <p>Eighteen letters have a numeral that can stand in for them. Eight do not, and a name that puts
            one of those where the numbers fall never reaches the Register. There is no official list: this
            is the substitution set PlateX's own engine uses, read from registrations that have really sold.</p>
        </div>
        <div class="alpha-grid">
          <div class="al" *ngFor="let a of alphabet"
               [class.none]="a.forms.length === 0" [class.dbl]="a.forms.length > 0 && a.forms[0].length > 1">
            <div class="from">{{ a.letter }}</div>
            <div class="to" *ngIf="a.forms.length > 0">{{ a.forms[0] }}</div>
            <div class="to" *ngIf="a.forms.length === 0">no form</div>
            <div class="ex" *ngIf="a.forms.length > 1">also {{ a.forms[1] }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ 3 · IN FOCUS ============ -->
  <section class="section specimen">
    <div class="wrap">
      <div class="sec-head between">
        <div><span class="eyebrow">In focus</span><h2 class="h-sec">One name, and a choice most people never get.</h2></div>
        <a class="more" href="#register" (click)="jumpTo('register', $event)">The Register <span class="arw">→</span></a>
      </div>
      <div class="grid">
        <div class="stage"><span class="plate lg"><span class="ch">J<span class="sub">4</span> MES</span></span></div>
        <div>
          <span class="eyebrow">James · one of the most given names in Britain</span>
          <h2>If your name is James, there are three ways to write it. Two are on the record.</h2>
          <p class="lead specimen-lead">Almost everyone in the Register gets one plate or none. James gets a
            choice: three legal arrangements, two of them registrations with their own history and their own
            owner. Choosing between them is not a technical decision. It is the same decision as choosing a
            watch: which version of the thing you already are do you want on the front of the car.</p>
          <div class="siblings">
            <div class="sib"><a [routerLink]="plateLink('J4MES')" class="plate-a"><span class="plate sm"><span class="ch">J<span class="sub">4</span> MES</span></span></a>
              <div class="sib-b"><div class="e">On the market</div><div class="d">The one people picture when they picture this plate.</div></div></div>
            <div class="sib"><a [routerLink]="plateLink('JAM3S')" class="plate-a"><span class="plate sm muted"><span class="ch">JAM <span class="sub">3</span>S</span></span></a>
              <div class="sib-b"><div class="e">Previously sold · issued 1977</div><div class="d">Older, quieter, and the name sits whole at the front.</div></div></div>
            <div class="sib"><span class="plate sm muted"><span class="ch">JAM <span class="sub">35</span></span></span>
              <div class="sib-b"><div class="e">No record of issue</div><div class="d">A legal arrangement, but the register holds no record of it. Dateless evidence is never complete.</div></div></div>
          </div>
          <!-- seller comparison removed (decision 4): comparison lives on the plate page -->
          <a class="more more--wrap" [routerLink]="plateLink('J4MES')">Compare sellers on the J4 MES page <span class="arw">→</span></a>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ 4 · STRUCTURAL SCARCITY ============ -->
  <section class="section section--paper findings" id="findings">
    <div class="wrap">
      <div class="sec-head findings-head">
        <div class="findings-title">
          <span class="eyebrow">Structural scarcity</span>
          <h2 class="h-sec">Why the Register stays small</h2>
        </div>
        <p class="lead findings-lead">The Register is not short because names are rare. It is short because the
          British registration system is closed in three specific ways, and none of them can be argued with.</p>
      </div>

      <!-- the two structural findings sit side by side: same reading order, half the height -->
      <div class="finding-pair">
        <div class="finding">
          <div class="fnum">7</div>
          <div class="fbody">
            <h3>A ceiling nobody can move</h3>
            <p>No British registration has ever exceeded seven characters. Christopher and Elizabeth are not
              waiting to be found. They cannot be reached, in any era, ever.</p>
          </div>
        </div>

        <div class="finding">
          <div class="fnum" *ngIf="currentShare as share">1 in {{ share }}</div>
          <div class="fnum fnum-pending" *ngIf="!currentShare" aria-hidden="true">—</div>
          <div class="fbody">
            <h3>The modern plate is the hostile one</h3>
            <p>Far more names reach a dateless plate than a current-style one. The middle characters must be a
              real age identifier, the letters can never include I or Q, and the name has to be exactly seven
              letters. New entries arrive rarely, and only twice a year.</p>
          </div>
        </div>
      </div>

      <!-- record sale reads across, plate beside the figure, rather than as a tall stack -->
      <div class="exhibit" *ngIf="recordSale as rec">
        <div class="ex-stage"><a class="plate-a" [routerLink]="plateLink(rec.registration)"><span class="plate lg"><span class="ch"><ng-container
          *ngFor="let c of chars(rec.display)"><span [class.sub]="c.sub">{{ c.ch }}</span></ng-container></span></span></a></div>
        <div class="ex-body">
          <span class="eyebrow">The record sale</span>
          <div class="fnum ex-fig">{{ '£' + (rec.pricePence / 100 | number:'1.0-0') }}</div>
          <h3>What the scarcity has been worth</h3>
          <p>{{ rec.display }}, or <b>{{ rec.name }}</b>, taken at the DVLA's own auction in May 2015: the most
            expensive current-style registration DVLA had ever sold. Current style, and from the era that
            produces the fewest names of all.</p>
          <p class="ex-src">Verified against the PlateX sold record: DVLA auction, {{ rec.date | date:'MMMM y' }}.
            A single recorded sale, not a trend.</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ 5 · FINDER (extracted component) ============ -->
  <pla-name-finder [reducedMotion]="reducedMotion"></pla-name-finder>

  <!-- ============ 6 · OUT OF REACH ============ -->
  <section class="section">
    <div class="wrap">
      <div class="sec-head">
        <span class="eyebrow">Out of reach</span>
        <h2 class="h-sec">The ones that got away</h2>
        <p class="lead reach-lead">Most names cannot be written, and the reasons are worth reading. A register
          that tells you why it says no can be trusted on the occasions it says yes.</p>
      </div>
      <div class="reach">
        <div class="reach-card">
          <div class="nm">Christopher</div>
          <p class="why">Eleven letters. British registrations stop at <b>seven characters</b>, and no longer
            format has ever been issued. There is no perfect arrangement, but there are still Christopher plates.</p>
          <button class="alt" (click)="searchFor('Christopher')">See available Christopher plates →</button>
        </div>
        <div class="reach-card">
          <div class="nm">Jack</div>
          <p class="why">Four letters, and three of them, <b>J</b>, <b>C</b> and <b>K</b>, have no numeral that
            resembles them. No perfect spelling reaches it, so start from what is on the market.</p>
          <button class="alt" (click)="searchFor('Jack')">See available Jack plates →</button>
        </div>
        <div class="reach-card">
          <div class="nm">Yvonne</div>
          <p class="why">Short enough, but the <b>Y</b> and the <b>V</b> fall on number slots in every
            six-character format, and neither has a numeral form. The nearest plates are a search away.</p>
          <button class="alt" (click)="searchFor('Yvonne')">See available Yvonne plates →</button>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ 7 · THE REGISTER · FRAMING ============ -->
  <section class="section section--tight regframe">
    <div class="wrap">
      <div class="sec-head regframe-head">
        <span class="eyebrow">The Register</span>
        <h2 class="h-sec">Every perfect name we have been able to find.</h2>
      </div>
      <div class="finding regframe-finding">
        <!-- live count from the API — never hard-coded (G1) -->
        <div class="fnum" *ngIf="totalUnfiltered !== null">{{ totalUnfiltered | number }}<small>names in the Register</small></div>
        <div class="fnum fnum-pending" *ngIf="totalUnfiltered === null" aria-hidden="true">—<small>names in the Register</small></div>
        <div class="fbody">
          <p class="regframe-p">Each one a British registration that spells a name exactly, and each one carrying
            evidence from the record: DVLA stock, a listing, a verified sale. We do not claim the Register is
            finished, because it is not. New series add to it twice a year, plates move between owners, and
            there are national name records we have not processed yet.</p>
          <p class="regframe-p strong">What we can say is that nobody has assembled more of it than this.</p>
        </div>
      </div>
      <div class="regnotes" *ngIf="travelNames.length || belongNames.length">
        <div class="rn">
          <h4>Names that travel</h4>
          <p>Short and vowel-led, and they belong to many places at once. These carry the broadest reach in the Register.</p>
          <div class="tchips">
            <span class="tchip" *ngFor="let t of travelNames.slice(0, 6)">
              {{ t.name }}<span class="tcount">{{ t.languages.length }} languages</span>
            </span>
          </div>
        </div>
        <div class="rn">
          <h4>Names that belong to one place</h4>
          <p>A single language, a single plate, and usually nobody looking for it. These are the entries the Register exists to record.</p>
          <div class="tchips">
            <span class="tchip" *ngFor="let b of belongNames.slice(0, 6)">
              <span class="fl">{{ flag(b.language) }}</span>{{ b.name }}<span class="tcount">{{ countryLabel(b.language) }}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ============ 7b · POPULARITY IS NOT VALUE ============
       Decision 6 required a verified popularity source before any popularity
       claim could ship. The ONS name records supply one (Open Government
       Licence), so the evidence band is live: rank from ONS, price from our own
       hammer-verified sales, joined at build time. -->
  <pla-names-popularity></pla-names-popularity>

  <!-- ============ 8 · REGISTER · TOOLBAR + A–Z + GRID (live) ============ -->
  <div class="toolbar" id="register">
    <div class="wrap">
      <span class="tb-lbl">Origin</span>
      <select class="tb-sel" (change)="setOrigin($any($event.target).value)" aria-label="Filter by origin">
        <option value="all">Every language</option>
        <option *ngFor="let o of originOptions" [value]="o.code" [selected]="origin === o.code">{{ o.label }}</option>
      </select>
      <span class="tb-lbl">Show</span>
      <div class="tb-group" role="group" aria-label="Availability">
        <button [class.active]="avail === 'all'" (click)="setAvail('all')">All</button>
        <button [class.active]="avail === 'resale'" (click)="setAvail('resale')">For sale</button>
        <button [class.active]="avail === 'held'" (click)="setAvail('held')">Held</button>
        <button [class.active]="avail === 'new-issue'" (click)="setAvail('new-issue')">New issue</button>
      </div>
      <span class="tb-count" *ngIf="total !== null && totalUnfiltered !== null"><b>{{ total | number }}</b> of {{ totalUnfiltered | number }} shown</span>
    </div>
  </div>
  <div class="azbar">
    <div class="wrap">
      <span class="azlinks">
        <button class="az-all" [class.active]="letter === 'all'" (click)="setLetter('all', $event)">All</button>
        <button *ngFor="let l of letters" [disabled]="!presentLetters.has(l)"
                [class.active]="letter === l" (click)="setLetter(l, $event)">{{ l }}</button>
      </span>
      <span class="legend">
        <span><i class="lg-ni"></i>New issue · DVLA</span>
        <span><i class="lg-av"></i>On the market</span>
        <span><i class="lg-hd"></i>Held or previously sold</span>
      </span>
    </div>
  </div>
  <div class="stale-note wrap" *ngIf="!availabilityLive && !loading && !loadFailed" role="status">
    Live availability is temporarily unavailable. Entries with an unconfirmed state show as
    "availability unknown" rather than guessing; verified sales are unaffected.
  </div>
  <section class="register">
    <div class="wrap">
      <!-- loading: skeleton grid, not an empty page -->
      <div class="reg-grid" *ngIf="loading" aria-hidden="true">
        <div class="item skel" *ngFor="let s of [0,1,2,3,4,5,6,7]">
          <span class="plate skel-plate"><span class="ch">&nbsp;</span></span>
          <div class="skel-line"></div>
          <div class="skel-line short"></div>
        </div>
      </div>

      <!-- error: message + retry, never a false empty state -->
      <div class="empty error" *ngIf="loadFailed && !loading">
        The register could not be loaded.
        <button class="btn btn--green retry" (click)="fetch(true)">Try again</button>
      </div>

      <div class="reg-grid" *ngIf="!loading && !loadFailed" [class.refreshing]="refreshing">
        <a class="item" *ngFor="let d of items; trackBy: trackByReg" [class.is-ni]="d.state === 'new-issue'"
           [routerLink]="plateLink(d.registration)"
           [attr.aria-label]="d.display + ' spells ' + d.primaryName">
          <span class="plate" [class.muted]="isMutedState(d)"><span class="ch"><ng-container
            *ngFor="let c of d.chars"><span [class.sub]="c.sub">{{ c.ch }}</span></ng-container></span></span>
          <div class="nm">{{ d.primaryName }}</div>
          <div class="also" *ngIf="d.names.length > 1">also {{ d.names.length - 1 }} more name{{ d.names.length > 2 ? 's' : '' }}</div>
          <div class="org"><span class="fl" *ngFor="let f of d.languages.slice(0, 4)" [attr.title]="countryLabel(f)">{{ flag(f) }}</span><span class="more-langs" *ngIf="d.languages.length > 4">+{{ d.languages.length - 4 }}</span></div>
          <div class="st" [class.avail]="d.state === 'resale'" [class.ni]="d.state === 'new-issue'"
               [class.held]="d.state === 'held'" [class.unknown]="d.state === 'unknown'">{{ stateLine(d) }}</div>
          <div class="sub-n">{{ d.eraLabel }}</div>
        </a>
      </div>

      <div class="empty" *ngIf="!loading && !loadFailed && items.length === 0">Nothing in the register matches that combination. Try widening the filters.</div>

      <div class="load-more" *ngIf="!loading && !loadFailed && hasMore">
        <button class="btn btn--ghost" (click)="loadMore()" [disabled]="loadingMore">
          {{ loadingMore ? 'Loading…' : 'Show more of the register' }}
        </button>
        <span class="lm-note">{{ items.length }} of {{ total | number }} loaded</span>
      </div>
    </div>
  </section>

  <!-- ============ 9 · HOW THE REGISTER IS CLASSIFIED ============ -->
  <section class="section section--tight">
    <div class="wrap">
      <div class="classify">
        <div class="top">
          <span class="eyebrow">How the Register is classified</span>
        </div>
        <div class="states">
          <div class="stt a"><div class="lb">New issue</div><div class="ds">Available directly from DVLA at fixed price. Never previously assigned.</div></div>
          <div class="stt c"><div class="lb">Resale</div><div class="ds">On the market through a verified dealer or private seller.</div></div>
          <div class="stt d"><div class="lb">Held</div><div class="ds">Evidence of issue, but not currently advertised anywhere.</div></div>
          <div class="stt e"><div class="lb">Previously sold</div><div class="ds">A verified sale exists in the record, with a date and a price.</div></div>
          <!-- auction-held row removed: decision 3 resolved, omitted from the public build -->
        </div>
      </div>
    </div>
  </section>

  <!-- ============ 10 · WATCHLIST (extracted component) ============ -->
  <pla-names-watchlist></pla-names-watchlist>
</div>
